import { useState } from 'react';
import { Save, Trash2, FolderOpen, ChevronDown, ChevronUp } from 'lucide-react';
import type { ValidationCase } from '../../types/barcodeValidation';

interface Props {
  cases: ValidationCase[];
  onSave: (name: string) => void;
  onLoad: (id: string) => void;
  onDelete: (id: string) => void;
}

export function SavedCasesManager({ cases, onSave, onLoad, onDelete }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [newName, setNewName] = useState('');

  const handleSave = () => {
    const name = newName.trim() || `Caso ${new Date().toLocaleString('pt-BR')}`;
    onSave(name);
    setNewName('');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5"
      >
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-teal-600" />
          Casos Salvos ({cases.length})
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          {/* Save new case */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nome do caso (opcional)"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
            >
              <Save className="w-4 h-4" />
              Salvar
            </button>
          </div>

          {/* List */}
          {cases.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Nenhum caso salvo</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {cases.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2.5"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(c.createdAt).toLocaleString('pt-BR')} — {c.photos.length} foto(s)
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <button
                      onClick={() => onLoad(c.id)}
                      className="p-1.5 text-teal-600 hover:bg-teal-100 rounded-lg transition-colors"
                      title="Carregar"
                    >
                      <FolderOpen className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onDelete(c.id)}
                      className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
